import { Instagram, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import SectionTitle from "@/components/SectionTitle";
import SEO from "@/components/SEO";

const INSTAGRAM_HANDLE = "iocri_world";
const INSTAGRAM_URL = `https://instagram.com/${INSTAGRAM_HANDLE}`;

export default function SocialMedia() {
  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12" data-testid="social-media-page">
      <SEO
        title="Social Media"
        description="Follow the International Organized Crime Research Institute on Instagram — @iocri_world."
        path="/social-media"
      />
      <SectionTitle
        eyebrow="SOCIAL MEDIA"
        title="Follow IOCRI across the internet."
        subtitle="Announcements, research previews, awareness campaigns and behind-the-scenes."
      />

      {/* Instagram spotlight — gold on black */}
      <div className="mt-12 grid md:grid-cols-[1fr,1fr] gap-6">
        <Card className="bg-black border border-gold/40 rounded-3xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(212,175,55,0.45)]" data-testid="instagram-card">
          <CardContent className="p-10 md:p-14">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl border border-gold/40 bg-black flex items-center justify-center">
                <Instagram className="h-6 w-6 text-gold" strokeWidth={1.5} />
              </div>
              <div className="text-gold font-mono text-xs tracking-[0.35em]">INSTAGRAM</div>
            </div>

            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="instagram-handle-link"
              className="mt-8 block group"
              aria-label={`Open @${INSTAGRAM_HANDLE} on Instagram`}
            >
              <div className="font-display text-4xl md:text-5xl leading-none text-gold-gradient tracking-tight group-hover:opacity-90 transition-opacity break-all inline-flex items-center gap-3">
                @{INSTAGRAM_HANDLE}
                <ExternalLink className="h-5 w-5 text-gold opacity-70 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="mt-2 text-[10px] font-mono tracking-[0.36em] text-gold/70">
                CLICK TO OPEN INSTAGRAM →
              </div>
            </a>

            <p className="mt-5 text-white/60 text-sm max-w-md">
              Our official Instagram — research highlights, event updates, and campaign posters.
            </p>

            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="instagram-follow-btn" className="inline-block mt-8">
              <Button className="rounded-full bg-gold text-black hover:bg-gold-light h-11 px-6 font-mono text-xs tracking-widest">
                FOLLOW @{INSTAGRAM_HANDLE} <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </CardContent>
        </Card>

        {/* Info side card */}
        <Card className="bg-obsidian-soft border-white/5 rounded-3xl" data-testid="social-info-card">
          <CardContent className="p-10 md:p-14 h-full flex flex-col">
            <div className="text-gold font-mono text-xs tracking-[0.35em]">STAY CONNECTED</div>
            <h3 className="font-display text-3xl md:text-4xl text-white mt-3 leading-tight">
              Where IOCRI shows up first.
            </h3>
            <p className="mt-4 text-white/70 leading-relaxed">
              Instagram is our fastest channel for scam alerts, event photos, and youth-led awareness content. More platforms coming soon.
            </p>
            <ul className="mt-6 space-y-2 text-white/70 text-sm">
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-gold" /> Scam-alert reels</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-gold" /> Research infographics</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-gold" /> Founder & team stories</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-gold" /> Live event coverage</li>
            </ul>
            <div className="mt-auto pt-8 text-white/40 text-xs font-mono">
              MORE PLATFORMS · COMING SOON
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
